import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import { Row, Stack } from '@raptormaps/layout';
import { Text } from '@raptormaps/text';

import { getSolarFarmByUuidQuery } from '../../api/services/solar-farm';
import { getAllOrgsQuery } from '../../api/services/orgs';
import { useSearchParams } from '../../hooks/useSearchParams';
import { SolarFarmTypeahead } from './SolarFarmTypeahead';
import { FarmPermissionsTable } from './FarmPermissionsTable';

export const FarmPermissions = () => {
  const history = useHistory();
  const searchParams = useSearchParams();

  const selectedFarmUuid = searchParams.get('farm');

  const { data: selectedFarm, isLoading } = useQuery({
    ...getSolarFarmByUuidQuery(selectedFarmUuid ?? ''),
    select: data => data.solar_farm,
    enabled: typeof selectedFarmUuid === 'string',
  });

  const { data: allOrgs } = useQuery({
    ...getAllOrgsQuery(),
    select: data => data.orgs,
  });

  const orgNameMap = useMemo(() => {
    const _orgNameMap = new Map<number, string>();
    allOrgs?.forEach(org => {
      _orgNameMap.set(org.id, org.name);
    });
    return _orgNameMap;
  }, [allOrgs]);

  // if the UUID in the URL doesn't match a valid farm, remove the UUID from the URL
  useEffect(() => {
    if (!isLoading && selectedFarmUuid && !selectedFarm) {
      searchParams.delete('farm');
      history.push({ search: searchParams.toString() });
    }
  }, [history, isLoading, searchParams, selectedFarm, selectedFarmUuid]);

  return (
    <Stack gutter="lg" style={{ width: '90%', margin: '4rem auto' }}>
      <Row justify="space-between">
        <SolarFarmTypeahead
          selectedFarm={selectedFarm}
          isLoadingSelectedFarm={isLoading}
        />
        {/* add org button will go here eventually */}
        <div />
      </Row>
      {selectedFarm ? (
        <>
          <Stack gutter="xxs">
            <Text variant="h3">{selectedFarm.name}</Text>
            <Text variant="paragraph_small_italic">{selectedFarm.id}</Text>
            <Text variant="paragraph_medium">{selectedFarm.address}</Text>
          </Stack>
          {selectedFarm.client_org_id ? (
            <FarmPermissionsTable
              solarFarmId={selectedFarm.id}
              solarFarmOrgId={selectedFarm.client_org_id}
              orgNameMap={orgNameMap}
            />
          ) : (
            <Text>
              This solar farm does not have a client org associated with it.
              Please add a client org to the farm to manage permissions.
            </Text>
          )}
        </>
      ) : null}
    </Stack>
  );
};
